import React, { useEffect, useMemo, useRef, useState } from 'react';
import Input from '@/components/ui/Input';
import galleryStyles from '@/styles/components/models-gallery/ModelsGalleryPage.module.css';
import styles from '@/styles/components/landing/GetAccessModal.module.css';
import type { LandingRequestPayload, LandingRequestType } from '@/types/landing-requests';
import generatedApiClient from '@/services/generatedApiClient';
import { tLanding, type LandingLang } from '@/i18n/landingTranslations';
import type {
  RequestsRequestAgentPlacementPost,
  RequestsRequestHardwareRentOutPost,
  RequestsRequestHardwareRentPost,
  RequestsRequestLLMBusinessPost,
  RequestsRequestLLMPersonalPost,
} from '@/generated/api';

type RequestType = LandingRequestType;

type BaseFields = {
  name: string;
  contact: string;
  type: RequestType | '';
};

type RentGpuFields = {
  targetGpuModel: string;
  gpuCount: string;
  minVram: string;
  expectedUptime: string;
};

type RentOutGpuFields = {
  gpuModelOut: string;
  gpuCountOut: string;
  totalVram: string;
  availableUptime: string;
};

type LlmBusinessFields = {
  modelTypeBusiness: string;
  modelNameBusiness: string;
  modelUrl: string;
  avgInputTokens: string;
  avgOutputTokens: string;
};

type LlmPersonalFields = {
  modelTypePersonal: string;
  modelNamePersonal: string;
};

type AgentPlacementFields = {
  agentDescription: string;
};

type FormState = BaseFields &
  RentGpuFields &
  RentOutGpuFields &
  LlmBusinessFields &
  LlmPersonalFields &
  AgentPlacementFields;

const emptyState: FormState = {
  name: '',
  contact: '',
  type: '',
  // rent-gpu
  targetGpuModel: '',
  gpuCount: '',
  minVram: '',
  expectedUptime: '',
  // rent-out-gpu
  gpuModelOut: '',
  gpuCountOut: '',
  totalVram: '',
  availableUptime: '',
  // llm-business
  modelTypeBusiness: '',
  modelNameBusiness: '',
  modelUrl: '',
  avgInputTokens: '',
  avgOutputTokens: '',
  // llm-personal
  modelTypePersonal: '',
  modelNamePersonal: '',
  // agent-placement
  agentDescription: '',
};

export interface GetAccessModalProps {
  lang?: LandingLang;
  isOpen: boolean;
  onClose: () => void;
}

const GetAccessModal: React.FC<GetAccessModalProps> = ({ lang = 'en', isOpen, onClose }) => {
  const [form, setForm] = useState<FormState>(emptyState);
  const [error, setError] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const typeRef = useRef<HTMLDivElement | null>(null);

  const t = (key: Parameters<typeof tLanding>[1]) => tLanding(lang, key);

  const requestTypeLabel = (type: RequestType) => {
    switch (type) {
      case 'rent-gpu':
        return t('formTypeRentGpu');
      case 'rent-out-gpu':
        return t('formTypeRentOutGpu');
      case 'agent-placement':
        return t('formTypeAgentPlacement');
      case 'llm-business':
        return t('formTypeLlmBusiness');
      case 'llm-personal':
        return t('formTypeLlmPersonal');
      default:
        return t('formTypeSelect');
    }
  };

  const selectedType = form.type as RequestType | '';

  const showTypeFields = useMemo(() => {
    return {
      rentGpu: selectedType === 'rent-gpu',
      rentOutGpu: selectedType === 'rent-out-gpu',
      agentPlacement: selectedType === 'agent-placement',
      llmBusiness: selectedType === 'llm-business',
      llmPersonal: selectedType === 'llm-personal',
    };
  }, [selectedType]);

  const update = (key: keyof FormState, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setError('');
  };

  useEffect(() => {
    if (!isTypeOpen) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!typeRef.current) return;
      if (!typeRef.current.contains(e.target as Node)) {
        setIsTypeOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isTypeOpen]);

  const handleClose = () => {
    if (isSubmitting) return;
    setForm(emptyState);
    setError('');
    setSubmitted(false);
    setIsTypeOpen(false);
    onClose();
  };

  const parseNumber = (value: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  };

  const validate = () => {
    if (!form.name.trim()) return t('errNameRequired');
    if (!form.contact.trim()) return t('errContactRequired');
    if (!form.type) return t('errTypeRequired');

    if (form.type === 'rent-gpu') {
      if (!form.targetGpuModel.trim()) return `${t('formTargetGpuModel')} — required.`;
      if (!form.gpuCount.trim()) return `${t('formGpuCount')} — required.`;
      if (!form.minVram.trim()) return `${t('formMinVram')} — required.`;
      if (!form.expectedUptime.trim()) return `${t('formExpectedUptime')} — required.`;
      const count = parseNumber(form.gpuCount);
      const vram = parseNumber(form.minVram);
      if (count === null || count <= 0) return 'GPU count must be a number > 0.';
      if (vram === null || vram <= 0) return 'VRAM must be a number > 0.';
    }

    if (form.type === 'rent-out-gpu') {
      if (!form.gpuModelOut.trim()) return `${t('formGpuModel')} — required.`;
      if (!form.gpuCountOut.trim()) return `${t('formGpuCountOut')} — required.`;
      if (!form.totalVram.trim()) return `${t('formTotalVram')} — required.`;
      if (!form.availableUptime.trim()) return `${t('formAvailableUptime')} — required.`;
      const count = parseNumber(form.gpuCountOut);
      const vram = parseNumber(form.totalVram);
      if (count === null || count <= 0) return 'GPU count must be a number > 0.';
      if (vram === null || vram <= 0) return 'VRAM must be a number > 0.';
    }

    if (form.type === 'llm-business') {
      if (!form.modelTypeBusiness.trim()) return `${t('formModelType')} — required.`;
    }

    if (form.type === 'llm-personal') {
      if (!form.modelTypePersonal.trim()) return `${t('formModelType')} — required.`;
    }

    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    const payload: LandingRequestPayload = {
      ...(form as any),
      type: form.type as RequestType,
    };

    setIsSubmitting(true);
    setError('');

    try {
      // eslint-disable-next-line no-console
      console.log('[GetAccessModal] submit', payload);

      // Persist locally for debugging/support
      try {
        localStorage.setItem('swarmind:get-access:last', JSON.stringify(payload));
      } catch {
        // ignore
      }

      const name = form.name.trim();
      const contact = form.contact.trim();

      // NOTE: backend schema uses `email` field. For now we pass the contact string through.
      // If backend starts strictly validating email, we can split into email/telegram later.
      const email = contact;

      let result: { error?: string; status: number };

      if (form.type === 'rent-gpu') {
        const req: RequestsRequestHardwareRentPost = {
          email,
          name,
          hardware_type: 'Other',
          gpu_model: form.targetGpuModel.trim(),
          gpu_count: Math.floor(parseNumber(form.gpuCount) || 0),
          vram_gb: Math.floor(parseNumber(form.minVram) || 0),
          uptime: form.expectedUptime.trim(),
          notes: undefined,
        };
        result = await (generatedApiClient as any).requestHardwareRent(req);
      } else if (form.type === 'rent-out-gpu') {
        const req: RequestsRequestHardwareRentOutPost = {
          email,
          name,
          hardware_type: 'Other',
          gpu_model: form.gpuModelOut.trim(),
          gpu_count: Math.floor(parseNumber(form.gpuCountOut) || 0),
          vram_gb: Math.floor(parseNumber(form.totalVram) || 0),
          uptime: form.availableUptime.trim(),
          notes: undefined,
        };
        result = await (generatedApiClient as any).requestHardwareRentOut(req);
      } else if (form.type === 'llm-business') {
        const req: RequestsRequestLLMBusinessPost = {
          email,
          name,
          model_type: form.modelTypeBusiness.trim(),
          model_name: form.modelNameBusiness.trim() || undefined,
          model_url: form.modelUrl.trim() || undefined,
          average_token_input: parseNumber(form.avgInputTokens) ?? undefined,
          average_token_output: parseNumber(form.avgOutputTokens) ?? undefined,
          notes: undefined,
        };
        result = await (generatedApiClient as any).requestLLMBusiness(req);
      } else if (form.type === 'llm-personal') {
        const req: RequestsRequestLLMPersonalPost = {
          email,
          name,
          model_type: form.modelTypePersonal.trim(),
          model_name: form.modelNamePersonal.trim() || undefined,
          notes: undefined,
        };
        result = await (generatedApiClient as any).requestLLMPersonal(req);
      } else {
        const req: RequestsRequestAgentPlacementPost = {
          email,
          name,
          notes: form.agentDescription.trim() || undefined,
        };
        result = await (generatedApiClient as any).requestAgentPlacement(req);
      }

      if (result?.error) {
        setError(result.error);
        return;
      }

      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={galleryStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="get-access-title"
      onClick={handleClose}
    >
      <div className={galleryStyles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 id="get-access-title" className={styles.title}>
            {t('getAccess')}
          </h2>
          <button type="button" className={styles.close} onClick={handleClose}>
            ×
          </button>
        </div>

        {submitted ? (
          <div className={styles.success}>
            <p className={styles.successTitle}>{t('successTitle')}</p>
            <p className={styles.successText}>{t('successText')}</p>
            <div className={styles.actions}>
              <button type="button" className={styles.secondaryButton} onClick={handleClose}>
                {t('close')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            {error && <div className={styles.error}>{error}</div>}

            <Input
              type="text"
              label={t('formName')}
              placeholder={t('formName')}
              value={form.name}
              onChange={e => update('name', e.target.value)}
            />

            <Input
              type="text"
              label={t('formContact')}
              placeholder={t('formContact')}
              value={form.contact}
              onChange={e => update('contact', e.target.value)}
            />

            <div className={styles.selectGroup}>
              <label className={styles.selectLabel} htmlFor="requestType">
                {t('formType')}
              </label>
              <div className={styles.customSelect} ref={typeRef}>
                <input type="hidden" value={form.type} />
                <button
                  type="button"
                  className={`${styles.selectSelected} ${form.type ? styles.hasValue : ''}`}
                  onClick={() => setIsTypeOpen(v => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={isTypeOpen}
                >
                  <span className={styles.selectSelectedText}>
                    {form.type ? requestTypeLabel(form.type as RequestType) : t('formTypeSelect')}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    className={`${styles.selectArrow} ${isTypeOpen ? styles.selectArrowOpen : ''}`}
                    aria-hidden="true"
                  >
                    <path fill="currentColor" d="M6 9L1 4h10z" />
                  </svg>
                </button>
                {isTypeOpen && (
                  <div className={styles.selectItems} role="listbox">
                    {(
                      [
                        'rent-gpu',
                        'rent-out-gpu',
                        'agent-placement',
                        'llm-business',
                        'llm-personal',
                      ] as RequestType[]
                    ).map(t => (
                      <button
                        key={t}
                        type="button"
                        className={`${styles.selectOption} ${form.type === t ? styles.optionSelected : ''}`}
                        onClick={() => {
                          update('type', t);
                          setIsTypeOpen(false);
                        }}
                        role="option"
                        aria-selected={form.type === t}
                      >
                        {requestTypeLabel(t)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {showTypeFields.rentGpu && (
              <div className={styles.fields}>
                <Input
                  type="text"
                  label={t('formTargetGpuModel')}
                  placeholder={t('formTargetGpuModel')}
                  value={form.targetGpuModel}
                  onChange={e => update('targetGpuModel', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formGpuCount')}
                  placeholder={t('formGpuCount')}
                  value={form.gpuCount}
                  onChange={e => update('gpuCount', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formMinVram')}
                  placeholder={t('formMinVram')}
                  value={form.minVram}
                  onChange={e => update('minVram', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formExpectedUptime')}
                  placeholder={t('formExpectedUptime')}
                  value={form.expectedUptime}
                  onChange={e => update('expectedUptime', e.target.value)}
                />
              </div>
            )}

            {showTypeFields.rentOutGpu && (
              <div className={styles.fields}>
                <Input
                  type="text"
                  label={t('formGpuModel')}
                  placeholder={t('formGpuModel')}
                  value={form.gpuModelOut}
                  onChange={e => update('gpuModelOut', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formGpuCountOut')}
                  placeholder={t('formGpuCountOut')}
                  value={form.gpuCountOut}
                  onChange={e => update('gpuCountOut', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formTotalVram')}
                  placeholder={t('formTotalVram')}
                  value={form.totalVram}
                  onChange={e => update('totalVram', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formAvailableUptime')}
                  placeholder={t('formAvailableUptime')}
                  value={form.availableUptime}
                  onChange={e => update('availableUptime', e.target.value)}
                />
              </div>
            )}

            {showTypeFields.llmBusiness && (
              <div className={styles.fields}>
                <Input
                  type="text"
                  label={t('formModelType')}
                  placeholder={t('formModelType')}
                  value={form.modelTypeBusiness}
                  onChange={e => update('modelTypeBusiness', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formModelName')}
                  placeholder={t('formModelName')}
                  value={form.modelNameBusiness}
                  onChange={e => update('modelNameBusiness', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formModelUrl')}
                  placeholder={t('formModelUrl')}
                  value={form.modelUrl}
                  onChange={e => update('modelUrl', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formAvgInputTokens')}
                  placeholder={t('formAvgInputTokens')}
                  value={form.avgInputTokens}
                  onChange={e => update('avgInputTokens', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formAvgOutputTokens')}
                  placeholder={t('formAvgOutputTokens')}
                  value={form.avgOutputTokens}
                  onChange={e => update('avgOutputTokens', e.target.value)}
                />
              </div>
            )}

            {showTypeFields.llmPersonal && (
              <div className={styles.fields}>
                <Input
                  type="text"
                  label={t('formModelType')}
                  placeholder={t('formModelType')}
                  value={form.modelTypePersonal}
                  onChange={e => update('modelTypePersonal', e.target.value)}
                />
                <Input
                  type="text"
                  label={t('formModelName')}
                  placeholder={t('formModelName')}
                  value={form.modelNamePersonal}
                  onChange={e => update('modelNamePersonal', e.target.value)}
                />
              </div>
            )}

            {showTypeFields.agentPlacement && (
              <div className={styles.fields}>
                <label className={styles.selectLabel} htmlFor="agentDesc">
                  {t('formAgentDescription')}
                </label>
                <textarea
                  id="agentDesc"
                  className={styles.textarea}
                  rows={4}
                  placeholder={t('formAgentDescriptionPlaceholder')}
                  value={form.agentDescription}
                  onChange={e => update('agentDescription', e.target.value)}
                />
              </div>
            )}

            <div className={styles.actions}>
              <button type="submit" className={styles.ctaButton} disabled={isSubmitting}>
                {isSubmitting ? t('submitting') : t('getAccess')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default GetAccessModal;


